/**
 * Delimited text: CSV, TSV, and whatever a spreadsheet put on the clipboard.
 *
 * Hand-written rather than taken from a dependency, for one reason that earns
 * its keep: the delimiter is decided by looking at the text, not at the file
 * name. A `.csv` exported by a European spreadsheet is semicolon-separated, a
 * `.csv` full of tabs is a TSV somebody renamed, and a paste out of Excel is
 * tab-separated with no name at all. Every one of those arrives here and has to
 * work, and a parser that takes the delimiter as a parameter just moves the
 * problem to its caller.
 *
 * The quoting rules are RFC 4180's, which is what every spreadsheet writes:
 * fields may be wrapped in double quotes, a doubled quote inside a quoted field
 * is a literal one, and a quoted field may contain the delimiter and newlines.
 */

export interface DelimitedResult {
  rows: string[][];
  delimiter: string;
  /** True when parsing stopped at the row cap rather than at the end of the text. */
  truncated: boolean;
}

const CANDIDATES = [",", "\t", ";", "|"] as const;

/**
 * Picks the delimiter by which candidate gives the most consistent row width.
 *
 * Frequency alone is not enough: prose full of commas beats a genuine
 * tab-separated file on count. What distinguishes a real delimiter is that it
 * appears the *same number of times* on almost every line, so the score is the
 * agreement of the counts rather than their size. A file with one column has no
 * delimiter at all, and comma is the harmless answer there.
 */
export function sniffDelimiter(text: string): string {
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim().length > 0).slice(0, 20);
  if (!lines.length) return ",";

  let best = ",";
  let bestScore = -1;
  for (const candidate of CANDIDATES) {
    const counts = lines.map((line) => countOutsideQuotes(line, candidate));
    const first = counts[0] ?? 0;
    if (first === 0) continue;
    const agreed = counts.filter((c) => c === first).length / counts.length;
    // Agreement decides; the number of fields breaks a tie, because a file that
    // splits into eight consistent columns on tab and two on comma is a TSV.
    const score = agreed * 10 + Math.min(first, 20) / 20;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

function countOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') i++;
      else quoted = !quoted;
    } else if (!quoted && ch === delimiter) count++;
  }
  return count;
}

/**
 * Parses delimited text into rows of raw strings.
 *
 * Values are not interpreted here — no numbers, no dates, no trimming beyond
 * the line endings. Inference is a separate pass that votes across a whole
 * column, and it cannot do that if the parser has already guessed at each cell
 * on its own. Per-value guessing is exactly how one row's `03/04/2026` becomes
 * March and the next becomes April.
 */
export function parseDelimited(text: string, opts: { delimiter?: string; maxRows?: number } = {}): DelimitedResult {
  // A UTF-8 BOM is otherwise part of the first heading, which turns `Client`
  // into a field nothing matches and a label with an invisible character in it.
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const delimiter = opts.delimiter ?? sniffDelimiter(body);
  const maxRows = opts.maxRows ?? Number.POSITIVE_INFINITY;

  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let sawAny = false;
  let truncated = false;

  const endField = () => {
    row.push(field);
    field = "";
    sawAny = true;
  };
  // Rows with something in them, which is what the cap counts. A file saved with a
  // run of `,,,` lines under the data has not got more rows than a plan holds.
  let counted = 0;
  const endRow = () => {
    endField();
    // A trailing newline produces one empty field, which is not a row.
    if (!(row.length === 1 && row[0] === "")) {
      rows.push(row);
      if (row.some((f) => f.trim() !== "")) counted++;
    }
    row = [];
    sawAny = false;
  };

  for (let i = 0; i < body.length; i++) {
    const ch = body[i]!;
    if (quoted) {
      if (ch === '"') {
        if (body[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"' && field === "") {
      quoted = true;
      continue;
    }
    if (ch === delimiter) {
      endField();
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && body[i + 1] === "\n") i++;
      endRow();
      if (counted >= maxRows) {
        truncated = hasContent(body, i + 1, delimiter);
        return { rows, delimiter, truncated };
      }
      continue;
    }
    field += ch;
  }
  if (field !== "" || quoted || sawAny || row.length) endRow();

  return { rows, delimiter, truncated };
}

/** Whether anything but separators and blank lines is left after the cap. */
function hasContent(body: string, from: number, delimiter: string): boolean {
  for (let j = from; j < body.length; j++) {
    const ch = body[j]!;
    if (ch !== delimiter && ch !== '"' && ch.trim() !== "") return true;
  }
  return false;
}
